import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  AvailableSireDto,
  FertWorkDetailRecord,
  useGetAvailableSiresQuery,
} from '@/app/services/fertWork';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

const PAGE_SIZE = 50;

type SireOption = {
  key: string;
  label: string;
  sireId: string;
  sireName: string;
  sireBreed: string;
  sireBreedId: string;
  semenType: string;
  collectionCode: string;
  accountName: string;
};

export type EditDonorModalProps = {
  visible: boolean;
  donorRecord: FertWorkDetailRecord | null;
  sessionDetails?: FertWorkDetailRecord[];
  aspirationDate?: string;
  fertilizationSiteId?: string;
  onHide: () => void;
  onSave: (updatedRecord: FertWorkDetailRecord) => void;
};

const mapApiSireToOption = (sire: AvailableSireDto): SireOption => ({
  key: `${sire.sireId}|${sire.semenType || ''}|${sire.collectionCode || ''}`,
  label: sire.sireName || '',
  sireId: sire.sireId,
  sireName: sire.sireName || '',
  sireBreed: sire.sireBreed || '',
  sireBreedId: sire.sireBreedId || '',
  semenType: sire.semenType || '',
  collectionCode: sire.collectionCode || '',
  accountName: sire.accountName || '',
});

export const EditDonorModal = ({
  visible,
  donorRecord,
  sessionDetails = [],
  aspirationDate,
  fertilizationSiteId,
  onHide,
  onSave,
}: EditDonorModalProps) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const showMassProduction = useFeatureFlag('fert_work_mass_production');
  const [formData, setFormData] = useState<FertWorkDetailRecord | null>(null);

  const massProductionStraws =
    formData?.totalOocytes && formData.totalOocytes > 0
      ? Math.ceil(formData.totalOocytes / 150)
      : 0;
  const massProductionPellet = massProductionStraws * 30;
  const [page, setPage] = useState(0);
  const [loadedApiSires, setLoadedApiSires] = useState<SireOption[]>([]);
  const [sireSearch, setSireSearch] = useState('');
  const hasMoreRef = useRef(true);
  const filterActive = sireSearch.trim().length > 0;

  const canQuerySires = visible && !!aspirationDate && !!fertilizationSiteId;
  const {
    data: siresPage,
    isLoading: isLoadingSires,
    isFetching,
  } = useGetAvailableSiresQuery(
    {
      date: aspirationDate!,
      fertilizationSiteId: fertilizationSiteId!,
      skip: page * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    { skip: !canQuerySires },
  );

  useEffect(() => {
    if (visible) {
      setPage(0);
      setLoadedApiSires([]);
      setSireSearch('');
      hasMoreRef.current = true;
    }
  }, [visible, aspirationDate, fertilizationSiteId]);

  useEffect(() => {
    if (!filterActive || isFetching || !hasMoreRef.current) return;
    setPage((prev) => prev + 1);
  }, [filterActive, isFetching, loadedApiSires.length]);

  useEffect(() => {
    if (!siresPage) return;

    const seen = new Set(loadedApiSires.map((s) => s.key));
    const newOptions: SireOption[] = [];

    for (const sire of siresPage.items) {
      const option = mapApiSireToOption(sire);
      if (!seen.has(option.key)) {
        seen.add(option.key);
        newOptions.push(option);
      }

      if (sire.backupSireId && sire.backupSireName) {
        const backupOption: SireOption = {
          key: `${sire.backupSireId}|${sire.semenType || ''}|${sire.collectionCode || ''}`,
          label: sire.backupSireName || '',
          sireId: sire.backupSireId,
          sireName: sire.backupSireName,
          sireBreed: '',
          sireBreedId: '',
          semenType: sire.semenType || '',
          collectionCode: sire.collectionCode || '',
          accountName: sire.accountName || '',
        };
        if (!seen.has(backupOption.key)) {
          seen.add(backupOption.key);
          newOptions.push(backupOption);
        }
      }
    }

    if (newOptions.length > 0) {
      setLoadedApiSires((prev) => [...prev, ...newOptions]);
    }

    hasMoreRef.current = (page + 1) * PAGE_SIZE < siresPage.totalCount;
  }, [siresPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (donorRecord) {
      setFormData({ ...donorRecord });
    }
  }, [donorRecord]);

  // Merge session sires (first, preserve breed) + loaded API sires
  const sireOptions = useMemo(() => {
    const seen = new Map<string, SireOption>();

    for (const detail of sessionDetails) {
      if (!detail.sireId) continue;
      const key = `${detail.sireId}|${detail.semenType || ''}|${detail.collectionCode || ''}`;
      if (seen.has(key)) continue;
      seen.set(key, {
        key,
        label: detail.sireName || '',
        sireId: detail.sireId,
        sireName: detail.sireName || '',
        sireBreed: detail.sireBreed || '',
        sireBreedId: detail.sireBreedId || '',
        semenType: detail.semenType || '',
        collectionCode: detail.collectionCode || '',
        accountName: detail.accountName || '',
      });
    }

    for (const option of loadedApiSires) {
      if (!seen.has(option.key)) {
        seen.set(option.key, option);
      }
    }

    return Array.from(seen.values());
  }, [sessionDetails, loadedApiSires]);

  const selectedSireKey = formData
    ? `${formData.sireId || ''}|${formData.semenType || ''}|${formData.collectionCode || ''}`
    : null;

  const handleSireChange = (key: string) => {
    if (!formData) return;
    const sire = sireOptions.find((s) => s.key === key);
    if (!sire) return;
    setFormData({
      ...formData,
      sireId: sire.sireId,
      sireName: sire.sireName,
      sireBreed: sire.sireBreed,
      sireBreedId: sire.sireBreedId,
      semenType: sire.semenType,
      collectionCode: sire.collectionCode,
    });
  };

  const handleSireListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (filterActive || isFetching || !hasMoreRef.current) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      setPage((prev) => prev + 1);
    }
  };

  const sireFilter = (option: SireOption, query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [option.sireName, option.sireBreed, option.semenType].some((field) =>
      field.toLowerCase().includes(q),
    );
  };

  const sireOptionTemplate = (option: SireOption) => (
    <div className="flex w-full items-center gap-3">
      <span
        className="min-w-0 flex-1 truncate font-medium"
        title={option.sireName}
      >
        {option.sireName || '-'}
      </span>
      <span
        className="w-20 shrink-0 truncate text-sm text-gray-500"
        title={option.semenType}
      >
        {option.semenType || '-'}
      </span>
      <span
        className="w-36 shrink-0 truncate text-sm text-gray-500"
        title={option.accountName}
      >
        {option.accountName || '-'}
      </span>
      <span
        className="w-16 shrink-0 truncate text-right text-sm text-gray-500"
        title={option.collectionCode}
      >
        {option.collectionCode || '-'}
      </span>
    </div>
  );

  const handleFieldChange = (field: keyof FertWorkDetailRecord, value: any) => {
    if (!formData) return;
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const sireChanged =
    formData && donorRecord && formData.sireId !== donorRecord.sireId;

  const handleSave = () => {
    if (!formData) return;

    if (sireChanged) {
      confirm({
        description: t(
          'fertWork.confirmSireChange',
          'Are you sure you want to change the sire from "{from}" to "{to}"?',
          {
            from: donorRecord?.sireName || 'None',
            to: formData.sireName || 'None',
          },
        ),
        title: t('fertWork.confirmSireChangeHeader', 'Confirm Sire Change'),
        confirmVariant: 'warning',
        onConfirm: () => {
          onSave(formData);
          onHide();
        },
      });
      return;
    }

    onSave(formData);
    onHide();
  };

  const parseMatTime = (matTime?: string): Date | null => {
    if (!matTime) return null;
    const utc = new Date(matTime);
    if (isNaN(utc.getTime())) return null;
    return new Date(
      utc.getUTCFullYear(),
      utc.getUTCMonth(),
      utc.getUTCDate(),
      utc.getUTCHours(),
      utc.getUTCMinutes(),
      utc.getUTCSeconds(),
    );
  };

  const formatMatTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  };

  if (!formData) return null;

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      <DialogContent className="md:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t('fertWork.editDonorRecord', 'Edit Donor Record')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="opuSiteName" className="font-medium">
              {t('fertWork.opuSite', 'OPU Site')}
            </label>
            <Input
              id="opuSiteName"
              value={formData.opuSiteName || ''}
              onChange={(e) => handleFieldChange('opuSiteName', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="matTime" className="font-medium">
              {t('fertWork.matTime', 'Mat Time')}
            </label>
            <Calendar
              id="matTime"
              value={parseMatTime(formData.matTime)}
              onChange={(e) =>
                handleFieldChange(
                  'matTime',
                  e.value ? formatMatTime(e.value) : null,
                )
              }
              showTime
              showIcon
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="donorName" className="font-medium">
              {t('fertWork.donorName', 'Donor Name')}
            </label>
            <Input
              id="donorName"
              value={formData.donorName || ''}
              onChange={(e) => handleFieldChange('donorName', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="donorLocation" className="font-medium">
              {t('fertWork.donorLocation', 'Donor Location')}
            </label>
            <Input
              id="donorLocation"
              value={formData.donorLocation || ''}
              onChange={(e) =>
                handleFieldChange('donorLocation', e.target.value)
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="donorBreed" className="font-medium">
              {t('fertWork.donorBreed', 'Donor Breed')}
            </label>
            <Input
              id="donorBreed"
              value={formData.donorBreed || ''}
              onChange={(e) => handleFieldChange('donorBreed', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="totalOocytes" className="font-medium">
              {t('fertWork.totalOocytes', 'Total Oocytes')}
            </label>
            <Input
              id="totalOocytes"
              type="number"
              min={0}
              value={formData.totalOocytes ?? ''}
              onChange={(e) =>
                handleFieldChange('totalOocytes', e.target.valueAsNumber || 0)
              }
            />
          </div>

          {formData.isSplit && (
            <div className="flex flex-col gap-2">
              <label htmlFor="splitOocytes" className="font-medium">
                {t('fertWork.splitOocytes', 'Split Oocytes')}
              </label>
              <Input
                id="splitOocytes"
                type="number"
                min={0}
                max={formData.totalOocytes}
                value={formData.splitOocytes ?? formData.totalOocytes ?? ''}
                onChange={(e) =>
                  handleFieldChange('splitOocytes', e.target.valueAsNumber || 0)
                }
                onBlur={(e) => {
                  const value = e.target.valueAsNumber;
                  if (!Number.isNaN(value)) {
                    handleFieldChange(
                      'splitOocytes',
                      Math.min(
                        formData.totalOocytes ?? value,
                        Math.max(0, value),
                      ),
                    );
                  }
                }}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label htmlFor="sireName" className="font-medium">
              {t('fertWork.sireName', 'Sire Name')}
            </label>
            <Combobox
              items={sireOptions}
              filter={sireFilter}
              autoComplete="none"
              value={
                sireOptions.find((option) => option.key === selectedSireKey) ??
                null
              }
              onValueChange={(option: SireOption | null) => {
                if (option) handleSireChange(option.key);
              }}
              itemToStringValue={(option: SireOption) => option.key}
              itemToStringLabel={(option: SireOption) => option.label}
              onOpenChange={(open) => {
                if (!open) setSireSearch('');
              }}
            >
              <ComboboxInput
                id="sireName"
                className="w-full"
                placeholder={
                  isLoadingSires
                    ? t('common.loading', 'Loading')
                    : t('fertWork.selectSire', 'Select a sire')
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSireSearch(e.target.value)
                }
              />
              <ComboboxContent className="w-[560px] max-w-[90vw]">
                <ComboboxEmpty>
                  {t('common.noItemsFound', 'No items found.')}
                </ComboboxEmpty>
                <ComboboxList onScroll={handleSireListScroll}>
                  {(option: SireOption) => (
                    <ComboboxItem key={option.key} value={option}>
                      {sireOptionTemplate(option)}
                    </ComboboxItem>
                  )}
                </ComboboxList>
                {isFetching && (
                  <div className="text-muted-foreground flex items-center justify-center gap-2 border-t py-1.5 text-xs">
                    <i className="pi pi-spin pi-spinner" />
                    {t('common.loading', 'Loading')}
                  </div>
                )}
              </ComboboxContent>
            </Combobox>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="sireBreed" className="font-medium">
              {t('fertWork.sireBreed', 'Sire Breed')}
            </label>
            <Input id="sireBreed" value={formData.sireBreed || ''} disabled />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="sireNote" className="font-medium">
              {t('fertWork.sireNote', 'Sire Note')}
            </label>
            <Input
              id="sireNote"
              value={formData.sireNote || ''}
              onChange={(e) => handleFieldChange('sireNote', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="semenComments" className="font-medium">
              {t('fertWork.semenComments', 'Semen Comments')}
            </label>
            <Input
              id="semenComments"
              value={formData.semenComments || ''}
              onChange={(e) =>
                handleFieldChange('semenComments', e.target.value)
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="semenType" className="font-medium">
              {t('fertWork.semenType', 'Semen Type')}
            </label>
            <Input id="semenType" value={formData.semenType || ''} disabled />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="pelletNumber" className="font-medium">
              {t('fertWork.pelletNumber', 'Pellet Number')}
            </label>
            <Input
              id="pelletNumber"
              type="number"
              min={0}
              value={formData.pelletNumber ?? ''}
              onChange={(e) =>
                handleFieldChange(
                  'pelletNumber',
                  Number.isNaN(e.target.valueAsNumber)
                    ? null
                    : e.target.valueAsNumber,
                )
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="semenUnits" className="font-medium">
              {t('fertWork.semenUnits', 'Unit #')}
            </label>
            <Input
              id="semenUnits"
              type="number"
              min={0}
              value={formData.semenUnits ?? 1}
              onChange={(e) =>
                handleFieldChange(
                  'semenUnits',
                  Number.isNaN(e.target.valueAsNumber)
                    ? null
                    : e.target.valueAsNumber,
                )
              }
            />
          </div>

          {showMassProduction && (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isMassProduction"
                  checked={!!formData.isMassProduction}
                  onCheckedChange={(checked) =>
                    setFormData((prev) =>
                      prev ? { ...prev, isMassProduction: !!checked } : prev,
                    )
                  }
                />
                <label htmlFor="isMassProduction" className="font-medium">
                  {t('fertWork.massProduction', 'Mass Production')}
                </label>
              </div>

              {formData.isMassProduction && (
                <>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="strawCount" className="font-medium">
                      {t('fertWork.straws', 'Straws')}
                    </label>
                    <Input
                      id="strawCount"
                      type="number"
                      min={0}
                      value={
                        formData.strawCountOverridden
                          ? (formData.strawCount ?? '')
                          : massProductionStraws || ''
                      }
                      onChange={(e) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                strawCount: Number.isNaN(e.target.valueAsNumber)
                                  ? null
                                  : e.target.valueAsNumber,
                                strawCountOverridden: true,
                              }
                            : prev,
                        )
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="pelletVolumeUl" className="font-medium">
                      {t('fertWork.pelletVolume', 'Pellet Volume')}
                    </label>
                    <InputGroup>
                      <InputGroupInput
                        id="pelletVolumeUl"
                        type="number"
                        min={0}
                        value={
                          formData.pelletVolumeOverridden
                            ? (formData.pelletVolumeUl ?? '')
                            : massProductionPellet || ''
                        }
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  pelletVolumeUl: Number.isNaN(
                                    e.target.valueAsNumber,
                                  )
                                    ? null
                                    : e.target.valueAsNumber,
                                  pelletVolumeOverridden: true,
                                }
                              : prev,
                          )
                        }
                      />
                      <InputGroupAddon align="inline-end">µL</InputGroupAddon>
                    </InputGroup>
                  </div>
                </>
              )}
            </>
          )}

          <div className="flex flex-col gap-2">
            <label htmlFor="accountName" className="font-medium">
              {t('fertWork.accountName', 'Account Name')}
            </label>
            <Input
              id="accountName"
              value={formData.accountName || ''}
              onChange={(e) => handleFieldChange('accountName', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="shipLabel" className="font-medium">
              {t('fertWork.shipLabel', 'Ship Label')}
            </label>
            <Input
              id="shipLabel"
              value={formData.shipLabel || ''}
              onChange={(e) => handleFieldChange('shipLabel', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="splitRatio" className="font-medium">
              {t('fertWork.splitRatio', 'Split Ratio')}
            </label>
            <Input
              id="splitRatio"
              value={formData.splitRatio || ''}
              onChange={(e) => handleFieldChange('splitRatio', e.target.value)}
              placeholder={t(
                'fertWork.splitRatioPlaceholder',
                'e.g., 1/2, 1/3',
              )}
            />
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="separateFert"
                checked={formData.separateFert}
                onCheckedChange={(checked) =>
                  handleFieldChange('separateFert', checked)
                }
              />
              <label
                htmlFor="separateFert"
                className="cursor-pointer font-medium"
              >
                {t('fertWork.separateFertilization', 'Separate Fertilization')}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isSplit"
                checked={formData.isSplit}
                onCheckedChange={(checked) =>
                  handleFieldChange('isSplit', checked)
                }
              />
              <label htmlFor="isSplit" className="cursor-pointer font-medium">
                {t('fertWork.isSplit', 'Is Split')}
              </label>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onHide}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave}>
            {t('common.saveChanges', 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
